import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { formatApiErrorDetail } from "../lib/apiClient";
import { Eye, EyeOff } from "lucide-react";

export default function LoginPage() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      nav("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-obsidian text-white">
      <div
        className="relative hidden md:block border-r border-cmdBorder"
        style={{
          backgroundImage:
            "linear-gradient(rgba(10,10,10,0.85), rgba(10,10,10,0.75)), url('https://images.pexels.com/photos/32945758/pexels-photo-32945758.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 grid-lines opacity-40" />
        <div className="relative h-full flex flex-col justify-between p-10">
          <div>
            <div className="mc-label mb-3" style={{ color: "#94A3B8" }}>Stadium Operations Command</div>
            <h1 className="font-head text-5xl lg:text-6xl font-black uppercase leading-none tracking-tight" style={{ color: "#FFFFFF" }}>
              Match<br /><span className="text-readyGreen">Command</span>
            </h1>
            <p className="max-w-md mt-6 leading-relaxed" style={{ color: "#CBD5E1" }}>
              Real-time command &amp; control for international cricket match operations —
              from pre-match readiness to live run-sheet execution.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {[
              { k: "Departments", v: "10" },
              { k: "Live Sheets", v: "1" },
              { k: "Uptime", v: "24/7" },
            ].map((s) => (
              <div key={s.k} className="mc-panel p-4">
                <div className="mc-label">{s.k}</div>
                <div className="mc-metric text-3xl mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-16">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form" autoComplete="off">
          <div className="mc-label">Access Terminal</div>
          <h2 className="font-head text-4xl font-black uppercase mt-2 mb-8 tracking-tight">Sign In</h2>

          <label className="mc-label" htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            data-testid="login-email"
            className="w-full bg-black border border-cmdBorder text-white p-3 mt-1 mb-4 rounded-none font-mono-live text-sm"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="off"
            placeholder="you@example.com"
            required
          />

          <label className="mc-label" htmlFor="password">Password</label>
          <div className="relative mt-1 mb-6">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              data-testid="login-password"
              className="w-full bg-black border border-cmdBorder text-white p-3 pr-12 rounded-none font-mono-live text-sm"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              placeholder="Enter your password"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              title={showPassword ? "Hide password" : "Show password"}
              className="absolute inset-y-0 right-0 px-3 flex items-center text-zinc-400 hover:text-white transition-colors"
              data-testid="login-toggle-password"
            >
              {showPassword ? <EyeOff className="w-4 h-4" strokeWidth={1.6} /> : <Eye className="w-4 h-4" strokeWidth={1.6} />}
            </button>
          </div>

          {error && (
            <div className="text-incidentRed text-sm border border-incidentRed/40 bg-incidentRed/10 p-3 mb-4" data-testid="login-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full bg-white text-black py-3 font-bold uppercase tracking-widest text-sm hover:bg-zinc-200 transition-colors duration-150 disabled:opacity-60"
          >
            {loading ? "Authenticating…" : "Enter Command Centre"}
          </button>

          <div className="mt-10 border-t border-cmdBorder pt-6">
            <div className="mc-label mb-2">Need Access?</div>
            <p className="text-xs text-zinc-500">Contact your Match Command administrator to have an account created for you.</p>
          </div>
        </form>
      </div>
    </div>
  );
}
